import { apiClient } from './base';

export interface User {
  UserID: string;
  UserName: string;
  EmailAddress?: string;
  FirstName?: string;
  LastName?: string;
  JobTitle?: string;
  Mobile?: string;
  Phone?: string;
  Fax?: string;
  UserType: string;
  UserCategory?: string;
  Enable: string;
  CompanyID: string;
  CompanyName?: string;
  IbuttonID?: string;
  DeviceUserid?: number;
  Signature?: string;
  ProfileImage?: string;
  /** Per-user pour-type restriction; null/absent = inherits company access. */
  AllowedMonitoringFunctions?: string | null;
  full_name?: string;
  is_enabled: boolean;
  user_type_name: string;
}

export interface UserCreate {
  UserName: string;
  Password: string;
  CompanyID: string;
  EmailAddress?: string;
  FirstName?: string;
  LastName?: string;
  JobTitle?: string;
  Mobile?: string;
  Phone?: string;
  Fax?: string;
  UserType: string;
  UserCategory?: string;
  Enable?: string;
  AllowedMonitoringFunctions?: string;
}

export interface UserUpdate {
  UserName?: string;
  EmailAddress?: string;
  FirstName?: string;
  LastName?: string;
  JobTitle?: string;
  Mobile?: string;
  Phone?: string;
  Fax?: string;
  UserType?: string;
  UserCategory?: string;
  Enable?: string;
  CompanyID?: string;
  /** Comma-joined pour types; "" clears the restriction (inherit company). */
  AllowedMonitoringFunctions?: string;
}

export interface UserChangePassword {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface UserResetPassword {
  new_password: string;
  confirm_password: string;
}

export interface UserListResponse {
  users: User[];
  total: number;
  page: number;
  size: number;
  total_pages: number;
}

export interface UserSearchParams {
  search?: string;
  user_type?: string;
  company_id?: string;
  enabled?: boolean;
  page?: number;
  size?: number;
}

export interface UserStats {
  total_users: number;
  active_users: number;
  inactive_users: number;
  super_admins: number;
  regular_users: number;
  users_by_company: Array<{
    company_name: string;
    user_count: number;
  }>;
}

/**
 * Users API service
 */
export const usersAPI = {
  /**
   * Get list of users with search and pagination
   */
  getUsers: async (params: UserSearchParams = {}): Promise<UserListResponse> => {
    const searchParams = new URLSearchParams();
    
    if (params.search) searchParams.append('search', params.search);
    if (params.user_type) searchParams.append('user_type', params.user_type);
    if (params.company_id) searchParams.append('company_id', params.company_id);
    if (params.enabled !== undefined) searchParams.append('enabled', params.enabled.toString());
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.size) searchParams.append('size', params.size.toString());
    
    const response = await apiClient.get(`/users/?${searchParams.toString()}`);
    return response.data;
  },

  /**
   * Get user by ID
   */
  getUserById: async (userId: string): Promise<User> => {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data;
  },

  /**
   * Create new user
   */
  createUser: async (userData: UserCreate): Promise<User> => {
    const response = await apiClient.post('/users/', userData);
    return response.data;
  },

  /**
   * Update user
   */
  updateUser: async (userId: string, userData: UserUpdate): Promise<User> => {
    const response = await apiClient.put(`/users/${userId}`, userData);
    return response.data;
  },

  /**
   * Delete user (soft delete)
   */
  deleteUser: async (userId: string): Promise<void> => {
    await apiClient.delete(`/users/${userId}`);
  },

  /**
   * Change password
   */
  changePassword: async (userId: string, passwordData: UserChangePassword): Promise<void> => {
    await apiClient.post(`/users/${userId}/change-password`, passwordData);
  },

  /**
   * Reset user password (Super Admin only)
   */
  resetPassword: async (userId: string, passwordData: UserResetPassword): Promise<void> => {
    await apiClient.post(`/users/${userId}/reset-password`, passwordData);
  },

  /**
   * Toggle user status
   */
  toggleUserStatus: async (userId: string): Promise<{ message: string; enabled: boolean }> => {
    const response = await apiClient.post(`/users/${userId}/toggle-status`);
    return response.data;
  },

  /**
   * Get user statistics
   */
  getUserStats: async (): Promise<UserStats> => {
    const response = await apiClient.get('/users/stats');
    return response.data;
  },

  /**
   * Upload user avatar
   */
  uploadAvatar: async (userId: string, file: File): Promise<{ filename: string; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/files/users/${userId}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }
};